import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/HomePage.vue'),
    meta: { public: true },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginPage.vue'),
    meta: { public: true },
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('@/views/VerifyEmailPage.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPasswordPage.vue'),
    meta: { public: true },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPasswordPage.vue'),
    meta: { public: true },
  },
  {
    // Public — anyone can browse the marketplace. Add-to-cart / add-to-favorites
    // bounce to login at the action (see SkinCard / ProductPage).
    path: '/catalog',
    name: 'Catalog',
    component: () => import('@/views/MarketplacePage.vue'),
    meta: { public: true },
  },
  {
    path: '/catalog/:id',
    name: 'ItemDetail',
    component: () => import('@/views/ProductPage.vue'),
    meta: { public: true },
  },
  {
    path: '/blog',
    name: 'Blog',
    component: () => import('@/views/BlogPage.vue'),
    meta: { public: true },
  },
  {
    path: '/blog/:id',
    name: 'BlogPost',
    component: () => import('@/views/BlogPostPage.vue'),
    meta: { public: true },
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('@/views/AboutPage.vue'),
    meta: { public: true },
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('@/views/ContactPage.vue'),
    meta: { public: true },
  },
  {
    path: '/faq',
    name: 'Faq',
    component: () => import('@/views/FaqPage.vue'),
    meta: { public: true },
  },
  {
    path: '/coming-soon',
    name: 'ComingSoon',
    component: () => import('@/views/ComingSoonPage.vue'),
    meta: { public: true },
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('@/views/LegalPage.vue'),
    meta: { public: true, doc: 'terms' },
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('@/views/LegalPage.vue'),
    meta: { public: true, doc: 'privacy' },
  },
  {
    path: '/cookies',
    name: 'Cookies',
    component: () => import('@/views/LegalPage.vue'),
    meta: { public: true, doc: 'cookies' },
  },
  {
    path: '/checkout',
    name: 'Checkout',
    component: () => import('@/views/CheckoutPage.vue'),
    // Cart contents live in Pinia + localStorage so the page itself doesn't
    // require auth to render. The unauth case is surfaced as a gap with a
    // "Log in" CTA — same UX as the other pre-flight requirements.
    meta: { public: true },
  },
  {
    path: '/orders',
    name: 'Orders',
    component: () => import('@/views/OrdersPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/orders/:id',
    name: 'OrderDetail',
    component: () => import('@/views/OrderDetailPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/orders/:id/invoice',
    name: 'OrderInvoice',
    component: () => import('@/views/InvoicePage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/wallet',
    name: 'Wallet',
    component: () => import('@/views/WalletPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/wallet/topup',
    name: 'WalletTopUp',
    component: () => import('@/views/WalletTopUpPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/wallet/topup/return',
    name: 'TopupReturn',
    component: () => import('@/views/TopupReturnPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/ProfilePage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/favorites',
    name: 'Favorites',
    component: () => import('@/views/FavoritesPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/inventory',
    name: 'Inventory',
    component: () => import('@/views/InventoryPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/history',
    name: 'PurchaseHistory',
    component: () => import('@/views/PurchaseHistoryPage.vue'),
    meta: { requiresAuth: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  if (!to.meta.requiresAuth) return true;

  const auth = useAuthStore();

  // Fetch user on first navigation if not yet initialized
  if (!auth.initialized) {
    await auth.fetchUser();
  }

  if (!auth.isAuthenticated()) {
    return { name: 'Login', query: { redirect: to.fullPath } };
  }

  return true;
});

export default router;
